import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import {
  ConversationMessageDraft,
  GetUserConversationMessageDraft,
  useCurrentUserProfile,
} from 'src/graphql';
import {
  DELETE_CONVERSATION_MESSAGE_DRAFT,
  GET_CONVERSATION_MESSAGE_DRAFT_QUERY,
} from 'src/graphql/entities/conversation-message-draft/conversation-message-draft.operations';
import {
  findServerDraftIdByScope,
  isOptimisticDraftId,
} from 'src/graphql/entities/conversation-message-draft/hooks/utils';
import { clearLocalDraftForConversation } from 'src/pages/Conversations/Chat/conversation-draft-storage';

export interface DeleteConversationMessageDraft {
  deleteConversationMessageDraft: { id: string }[];
}

export const useDeleteConversationMessageDraft = () => {
  const { user } = useCurrentUserProfile();

  const [mutate, { client: apolloClient, loading }] =
    useMutation<DeleteConversationMessageDraft>(DELETE_CONVERSATION_MESSAGE_DRAFT);

  const evictDraftFromCache = useCallback(
    (id: string) => {
      const cacheId = apolloClient.cache.identify({
        __typename: 'ConversationMessageDraft',
        id,
      });
      if (cacheId) apolloClient.cache.evict({ id: cacheId });
      apolloClient.cache.updateQuery<GetUserConversationMessageDraft>(
        { query: GET_CONVERSATION_MESSAGE_DRAFT_QUERY },
        (existing) =>
          existing
            ? {
                getUserConversationMessageDraft: {
                  records: existing.getUserConversationMessageDraft.records.filter(
                    (d) => d.id !== id,
                  ),
                },
              }
            : existing,
      );
      apolloClient.cache.gc();
    },
    [apolloClient],
  );

  const deleteOne = useCallback(
    async (
      draft: Pick<
        ConversationMessageDraft,
        'id' | 'conversationId' | 'isPrivate' | 'isScheduled'
      >,
    ) => {
      let id = draft.id;

      if (isOptimisticDraftId(id)) {
        const realId = await findServerDraftIdByScope(apolloClient, {
          conversationId: draft.conversationId,
          isPrivate: Boolean(draft.isPrivate),
          isScheduled: Boolean(draft.isScheduled),
        });

        if (!realId) {
          // nothing persisted on the server for this scope, drop local copies only
          evictDraftFromCache(draft.id);
          clearLocalDraftForConversation(draft.conversationId);
          return;
        }

        id = realId;
      }

      await mutate({
        variables: { conditions: [{ attribute: 'id', operator: '=', value: id }] },
        optimisticResponse: { deleteConversationMessageDraft: [{ id }] },
        update: () => evictDraftFromCache(id),
      });
      clearLocalDraftForConversation(draft.conversationId);
    },
    [apolloClient, evictDraftFromCache, mutate],
  );

  const deleteAllByUserId = useCallback(async () => {
    const userId = user?.userId;

    if (!userId) return;

    // snapshot ids to evict
    const list =
      apolloClient.readQuery<GetUserConversationMessageDraft>({
        query: GET_CONVERSATION_MESSAGE_DRAFT_QUERY,
      })?.getUserConversationMessageDraft.records ?? [];

    const ids = list.map((d) => d.id);

    apolloClient.cache.updateQuery<GetUserConversationMessageDraft>(
      { query: GET_CONVERSATION_MESSAGE_DRAFT_QUERY },
      () => ({ getUserConversationMessageDraft: { records: [] } }),
    );

    ids.forEach((id) => {
      const ref = apolloClient.cache.identify({ __typename: 'ConversationMessageDraft', id });

      if (ref) apolloClient.cache.evict({ id: ref });
    });

    list.forEach((draft) => clearLocalDraftForConversation(draft.conversationId));

    apolloClient.cache.gc();

    await mutate({
      variables: { conditions: [{ attribute: 'userId', operator: '=', value: userId }] },
    });
  }, [apolloClient, mutate, user?.userId]);

  return {
    deleteConversationMessageDraft: deleteOne,
    deleteAllConversationMessageDrafts: deleteAllByUserId,
    isLoading: loading,
  };
};
